import { useMemo, useState } from 'react';
import type { FertilizerPack } from '@practium/features/api';
import {
	getPackProducts,
	isPriceInformed,
	productKey,
	type ProductPriceMap,
} from '../utils/productCosts';

export type ProductCostsModalMode = 'edit' | 'view';

export interface PlanningCostsSnapshot {
	prices: ProductPriceMap;
	pricesUpdatedAt: string | null;
}

interface UsePlanningCostsOptions {
	initial: PlanningCostsSnapshot | null;
	selectedPack: FertilizerPack | null;
	fertilizers: Array<{ _id: string; name?: string }>;
	isView: boolean;
}

export const usePlanningCosts = ({
	initial,
	selectedPack,
	fertilizers,
	isView,
}: UsePlanningCostsOptions) => {
	const [prices, setPrices] = useState<ProductPriceMap>(() => initial?.prices ?? {});
	const [pricesUpdatedAt, setPricesUpdatedAt] = useState<string | null>(
		() => initial?.pricesUpdatedAt ?? null
	);
	const [costsModal, setCostsModal] = useState<{ open: boolean; mode: ProductCostsModalMode }>({
		open: false,
		mode: 'edit',
	});

	const packProducts = useMemo(
		() => getPackProducts(selectedPack, fertilizers),
		[fertilizers, selectedPack]
	);
	const informedCostsCount = packProducts.filter((product) =>
		isPriceInformed(prices[productKey(product.id)])
	).length;
	const isCostsComplete = packProducts.length > 0 && informedCostsCount === packProducts.length;

	const restoreCosts = (snapshot: PlanningCostsSnapshot | null) => {
		setPrices(snapshot?.prices ?? {});
		setPricesUpdatedAt(snapshot?.pricesUpdatedAt ?? null);
	};

	const buildProductPrices = () =>
		packProducts
			.map((product) => prices[productKey(product.id)])
			.filter(isPriceInformed)
			.map(({ id, price, unit, bagWeightKg }) => ({
				id,
				price,
				unit,
				...(unit === 'bag' ? { bagWeightKg } : {}),
			}));

	const openCostsModal = () =>
		setCostsModal({ open: true, mode: isCostsComplete || isView ? 'view' : 'edit' });

	const closeCostsModal = () => setCostsModal((current) => ({ ...current, open: false }));

	const editCosts = () => setCostsModal({ open: true, mode: 'edit' });

	const handleCostsSaved = (nextPrices: ProductPriceMap) => {
		setPrices((current) => {
			const packKeys = new Set(packProducts.map((product) => productKey(product.id)));
			const kept = Object.fromEntries(
				Object.entries(current).filter(([key]) => !packKeys.has(key))
			);
			return { ...kept, ...nextPrices };
		});
		setPricesUpdatedAt(new Date().toISOString());
		setCostsModal({ open: true, mode: 'view' });
	};

	return {
		packProducts,
		prices,
		pricesUpdatedAt,
		informedCostsCount,
		isCostsComplete,
		costsModal,
		openCostsModal,
		closeCostsModal,
		editCosts,
		handleCostsSaved,
		restoreCosts,
		buildProductPrices,
	};
};
